import React, { useMemo, useEffect } from 'react';
import notFoundImage from "../../../assets/fox-not-found.webp";

import { getImageCacheKey, getImageUrl } from "../../../lib/helpers";
import { retireBlockedReason, filtrarPorPildora, mensajeSinProductos, esRetirado, stockBloqueadoMotivo } from "../../../lib/helpers/products";
import PildorasCategoria from "./PildorasCategoria";

// Icono por tipo de ubicación (almacén principal / nevera / armario / sub-almacén)
const LOC_ICON = { principal: '🏠', nevera: '🧊', armario: '🗄️', sub_almacen: '📦' };
const locIcon = (kind) => LOC_ICON[kind] || '📍';
const fmtQty = (n) => Number(n || 0).toLocaleString('es-ES', { maximumFractionDigits: 3 });

const ProductGrid = ({
  products,
  loading,
  onEdit,
  onDelete,
  onStock,
  onToggleActive,
  onToggleSelect,
  selectedIds = [],
  refreshTrigger,
  readOnly = false,
  canEdit = true,
  searchQuery = "",
  onVisibleProductsChange,
  selectedCategory = "All",
  onCategoryChange,
  categories: externalCategories,
  /** Almacén apaisado: 5 tarjetas por fila (como dispensario/productos) */
  landscapeCols = null,
  /** Desglose de stock por ubicación: { product_id: [{id,name,kind,qty}] } */
  stockByLoc = {},
}) => {
  // Categories are always provided by the parent (Products.jsx) which fetches them
  // with the correct x-venue-id header. No independent fetch here to avoid races.
  // Solo un array vale: con cualquier otra cosa (un `{}` de un mock o de un
  // backend a medio desplegar) `.filter` reventaba la pantalla entera.
  const allCategories = useMemo(() => (Array.isArray(externalCategories) ? externalCategories : []), [externalCategories]);

  // Compartido con ProductTable: la selección masiva opera sobre lo visible, así
  // que las dos vistas DEBEN filtrar idéntico (ver helper).
  const filteredProducts = useMemo(
    () => filtrarPorPildora(products, selectedCategory),
    [products, selectedCategory]
  );

  useEffect(() => {
    if (typeof onVisibleProductsChange !== 'function') return;
    const ids = (filteredProducts || []).map((p) => p.id);
    onVisibleProductsChange(ids);
  }, [filteredProducts, onVisibleProductsChange]);

  if (loading) {
    return (
      <div className="wa-almacen-grid-shell p-4">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-500">Cargando productos...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Category filter buttons - SIEMPRE visible */}
      <div className="wa-almacen-filters p-4">
        <PildorasCategoria categories={allCategories} selected={selectedCategory} onChange={onCategoryChange} />
      </div>

      {/* Mensaje cuando no hay productos filtrados */}
      {(!filteredProducts || filteredProducts.length === 0) && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-500">
          {mensajeSinProductos(searchQuery, selectedCategory)}
        </div>
      )}

      {filteredProducts && filteredProducts.length > 0 && (
      <div
        className={
          landscapeCols === 5
            ? "wa-almacen-products-grid wa-almacen-products-grid--five grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5 lg:gap-6"
            : "grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5 lg:gap-6"
        }
      >
          {filteredProducts.map((product, idx) => {
            const imageUrl = getImageUrl(product.image_path, notFoundImage, {
              cacheKey: getImageCacheKey('product', product.id),
            });
            const retirado = esRetirado(product);
            // `filtrarPorPildora` ya dejó los retirados al final: el primero abre la zona.
            const abreRetirados = retirado && (idx === 0 || !esRetirado(filteredProducts[idx - 1]));

            return (
              <React.Fragment key={product.id}>
              {abreRetirados && (
                <div data-testid="separador-retirados" className="wa-almacen-retirados-sep col-span-full">
                  Retirados ({filteredProducts.filter(esRetirado).length})
                </div>
              )}
              <div data-testid="producto-card" className={`wa-almacen-card${retirado ? ' wa-almacen-card--retirado' : ''} relative p-2 border rounded-lg flex flex-col`}>
                {!readOnly ? (
                  <label className="absolute left-3 top-3 z-10 flex h-6 w-6 items-center justify-center rounded bg-white/95 shadow">
                    <input
                      type="checkbox"
                      checked={selectedIds.some((id) => String(id) === String(product.id))}
                      onChange={() => onToggleSelect(product.id)}
                      className="h-4 w-4 accent-blue-600"
                    />
                  </label>
                ) : null}
                <div className="wa-almacen-image overflow-hidden rounded mb-2 bg-gray-100 flex-shrink-0">
                  <img
                    src={imageUrl}
                    alt={product.name}
                    loading="lazy"
                    decoding="async"
                    onError={e => { e.currentTarget.src = notFoundImage; }}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <h3 className="font-bold text-base truncate mb-1 leading-tight">{product.name}{product.is_active === false && (<span data-testid="badge-retirado" className="ml-2 align-middle rounded-full bg-[#F5F5F5] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#737373]" title="Retirado: no aparece en dispensario, productos ni en la carta del socio">Retirado</span>)}</h3>
                <p className="text-xs text-gray-500 mb-1">
                    <span className="wa-almacen-category-chip px-2 py-0.5 rounded-full text-xs font-semibold">
                    {(() => {
                      const n = product.category?.name;
                      if (!n) return 'Sin categoría';
                      return ['Uncategorized', 'uncategorized'].includes(n) ? 'Sin categoría' : n;
                    })()}
                  </span>
                </p>
                <p className="text-base font-bold text-gray-900 mb-1">
                  €{product.price || '0.00'}
                  {product.unit_type === 'fixed' ? '' : '/g'}
                </p>
                {!readOnly ? (
                  <>
                    <p className={`wa-almacen-stock text-xs font-semibold mb-2 ${
                      (product.available ?? product.stock_quantity ?? 0) > 10
                        ? 'text-green-600'
                        : (product.available ?? product.stock_quantity ?? 0) > 0
                        ? 'text-yellow-600'
                        : 'text-red-600'
                    }`}>
                      Stock: {product.available ?? product.stock_quantity ?? 0}
                    </p>
                    {(stockByLoc[product.id]?.length > 0) && (
                      <div className="flex flex-wrap gap-1 mb-2 -mt-1">
                        {stockByLoc[product.id].map((l) => {
                          const neg = Number(l.qty) < 0;
                          return (
                          <span
                            key={l.id}
                            className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium ${neg ? 'bg-red-50 text-[#DC2626] ring-1 ring-red-200' : 'bg-gray-100 text-gray-600'}`}
                            title={neg ? `${l.name} · en negativo, pendiente de reponer` : l.name}
                          >
                            <span aria-hidden>{neg ? '⚠️' : locIcon(l.kind)}</span>
                            <span className="max-w-[80px] truncate">{l.name}</span>
                            <span className={`font-semibold ${neg ? 'text-[#DC2626]' : 'text-gray-800'}`}>{fmtQty(l.qty)}{product.unit_type === 'fixed' ? 'u' : 'g'}</span>
                          </span>
                          );
                        })}
                      </div>
                    )}
                    {canEdit && (
                      <div className="flex gap-2 mt-auto">
                        <button
                          onClick={() => onEdit(product.id)}
                          className="wa-almacen-btn-edit flex-1 py-1.5 px-2 text-xs font-semibold transition-colors"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => onDelete(product.id)}
                          className="wa-almacen-btn-delete flex-1 py-1.5 px-2 text-xs font-semibold transition-colors"
                        >
                          Eliminar
                        </button>
                      </div>
                    )}
                    {canEdit && onStock && (
                      <button
                        onClick={() => onStock(product.id)}
                        disabled={retirado}
                        className="mt-1.5 w-full py-1.5 px-2 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:cursor-not-allowed disabled:grayscale disabled:hover:bg-emerald-600"
                        title={stockBloqueadoMotivo(product) || "Entrar / ajustar stock e historial"}
                      >
                        Actualizar stock
                      </button>
                    )}
                    {/* Retirar/Reactivar: la alternativa NO destructiva a Eliminar. */}
                    {canEdit && onToggleActive && (
                      <button
                        onClick={() => onToggleActive(product)}
                        className={`mt-1.5 w-full py-1.5 px-2 text-xs font-semibold rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                          product.is_active === false
                            ? 'bg-[#0A0A0A] hover:bg-black text-white'
                            : 'border border-[#E5E5E5] text-[#0A0A0A] hover:bg-[#F5F5F5]'
                        }`}
                        title={product.is_active === false
                          ? 'Volver a ponerlo a la venta'
                          : (retireBlockedReason(product) || 'Retirar de dispensario y productos sin borrarlo')}
                      >
                        {product.is_active === false ? '👁 Reactivar' : '🚫 Retirar'}
                      </button>
                    )}
                  </>
                ) : null}
              </div>
              </React.Fragment>
            );
          })}
      </div>
      )}
    </div>
  );
};

export default ProductGrid;
